// AGC: ConsultationDetail - Single consultation view
// AGC: Shows status, bids, messages, and deliverables

import React, { useState, useEffect } from 'react'
import { 
  MessageCircle, 
  Clock, 
  CheckCircle,
  Star,
  Send,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type Consultation,
  type ConsultationBid,
  STATUS_INFO,
  CONSULTATION_TYPE_INFO,
  URGENCY_INFO,
} from '../../types/advisor'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'

interface ConsultationDetailProps {
  consultationId?: string
  onNavigate: (screen: Screen, params?: Record<string, unknown>) => void
  onBack: () => void
}

const BACKEND_URL = getBackendUrl()

/**
 * AGC: Consultation Detail Component
 */
export const ConsultationDetail: React.FC<ConsultationDetailProps> = ({
  consultationId,
  onNavigate,
  onBack,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [consultation, setConsultation] = useState<Consultation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [showRating, setShowRating] = useState(false)
  const [rating, setRating] = useState(5)
  const [review, setReview] = useState('')

  /**
   * AGC: Fetch consultation
   */
  const fetchConsultation = async () => {
    if (!consultationId) {
      setError('ID da consulta não fornecido')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/advisors/consultations/${consultationId}`)
      const data = await response.json()

      if (data.success) {
        setConsultation(data.consultation)
      } else {
        setError(data.error || 'Erro ao carregar consulta')
      }
    } catch (err) {
      console.error('Error fetching consultation:', err)
      setError('Erro de conexão')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchConsultation()
  }, [consultationId])

  /**
   * AGC: Accept a bid
   */
  const handleAcceptBid = async (bidId: string) => {
    if (!consultation || !user?.uid) return
    
    setIsAccepting(true)
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/advisors/consultations/${consultationId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bidId,
          farmerId: user.uid,
        }),
      })

      const data = await response.json()

      if (data.success) {
        fetchConsultation()
      } else {
        setError(data.error || 'Erro ao aceitar proposta')
      }
    } catch (err) {
      console.error('Error accepting bid:', err)
      setError('Erro de conexão')
    } finally {
      setIsAccepting(false)
    }
  }

  /**
   * AGC: Complete consultation with rating
   */
  const handleComplete = async () => {
    if (!consultation || !user?.uid) return
    
    setIsCompleting(true)
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/advisors/consultations/${consultationId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farmerId: user.uid,
          rating,
          review,
        }),
      })

      const data = await response.json()

      if (data.success) {
        setShowRating(false)
        fetchConsultation()
      } else {
        setError(data.error || 'Erro ao concluir consulta')
      }
    } catch (err) {
      console.error('Error completing consultation:', err)
      setError('Erro de conexão')
    } finally {
      setIsCompleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    )
  }

  if (error || !consultation) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900">
        <TopBar title="Detalhes" onBack={onBack} />
        <div className="p-4">
          <GlassCard>
            <div className="text-center py-6">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-500">{error || 'Consulta não encontrada'}</p>
              <button onClick={onBack} className="mt-4 text-emerald-600">
                Voltar
              </button>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  const statusInfo = STATUS_INFO[consultation.status]
  const typeInfo = CONSULTATION_TYPE_INFO[consultation.type]
  const urgencyInfo = URGENCY_INFO[consultation.urgency]

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('advisor.consultationDetail') || 'Detalhes da Consulta'}
        onBack={onBack}
      />

      <div className="p-4 pb-32 space-y-4">
        {/* AGC: Status header */}
        <GlassCard>
          <div className="flex items-center justify-between mb-4">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
              {statusInfo.name}
            </span>
            <span className={`text-sm ${urgencyInfo.color}`}>
              ● {urgencyInfo.name}
            </span>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {consultation.title}
          </h2>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {typeInfo.name} • Criado em {new Date(consultation.createdAt).toLocaleDateString('pt-BR')}
          </p>
          
          <p className="text-gray-600 dark:text-gray-400">
            {consultation.description}
          </p>

          {/* AGC: Budget */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Orçamento</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              R$ {consultation.budget.min} - R$ {consultation.budget.max}
            </p>
          </div>
        </GlassCard>

        {/* AGC: Consultant info if assigned */}
        {consultation.consultantId && (
          <GlassCard>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Consultor Designado
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {consultation.consultantName?.charAt(0) || 'C'}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {consultation.consultantName}
                </p>
                {consultation.proposedPrice && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Valor acordado: R$ {consultation.proposedPrice}
                  </p>
                )}
              </div>
            </div>
          </GlassCard>
        )}

        {/* AGC: Bids section (if in bidding status) */}
        {['pending', 'bidding'].includes(consultation.status) && (
          <GlassCard padding="none">
            <div className="p-4 border-b border-gray-200 dark:border-slate-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Propostas ({consultation.bids?.length || 0})
              </h3>
            </div>
            
            {(!consultation.bids || consultation.bids.length === 0) ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aguardando propostas de consultores...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-slate-700">
                {consultation.bids.map((bid: ConsultationBid) => (
                  <div key={bid.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                          <span className="text-white font-bold">
                            {bid.consultantName?.charAt(0) || 'C'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {bid.consultantName}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span>{bid.consultantRating?.toFixed(1) || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600 dark:text-emerald-400">
                          R$ {bid.price}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {bid.estimatedDelivery}
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {bid.message}
                    </p>
                    
                    {bid.status === 'pending' && (
                      <button
                        onClick={() => handleAcceptBid(bid.id)}
                        disabled={isAccepting}
                        className="w-full py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {isAccepting ? 'Aceitando...' : 'Aceitar Proposta'}
                      </button>
                    )}
                    
                    {bid.status === 'accepted' && (
                      <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Proposta aceita</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        )}

        {/* AGC: Deliverable (if delivered) */}
        {consultation.deliverable && (
          <GlassCard>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Entrega
            </h3>
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
              <p className="text-gray-700 dark:text-gray-300">
                {consultation.deliverable}
              </p>
            </div>
          </GlassCard>
        )}

        {/* AGC: Rating (if completed) */}
        {consultation.rating && (
          <GlassCard>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Sua Avaliação
            </h3>
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  className={`w-6 h-6 ${
                    star <= consultation.rating!
                      ? 'text-yellow-500 fill-yellow-500'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            {consultation.review && (
              <p className="text-gray-600 dark:text-gray-400">
                "{consultation.review}"
              </p>
            )}
          </GlassCard>
        )}
      </div>

      {/* AGC: Action button based on status */}
      {consultation.status === 'delivered' && !consultation.rating && (
        <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-safe-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
          {!showRating ? (
            <button
              onClick={() => setShowRating(true)}
              className="w-full py-4 bg-emerald-600 text-white rounded-xl font-semibold"
            >
              Aprovar e Avaliar
            </button>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Avalie o consultor:</p>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= rating
                            ? 'text-yellow-500 fill-yellow-500'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Comentário (opcional)"
                rows={2}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 rounded-xl"
              />
              
              <button
                onClick={handleComplete}
                disabled={isCompleting}
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {isCompleting ? 'Enviando...' : 'Concluir Consulta'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ConsultationDetail

